import React, { useEffect } from 'react';
import { Play, Repeat } from 'lucide-react';
import { useMusicPlayer } from '../context/MusicPlayerContext';

// Presentational bento widget only — all playback state (and the single
// shared <audio> element) lives in MusicPlayerProvider so a track keeps
// playing after this widget unmounts (e.g. leaving the About section).
const MusicPlayer: React.FC = () => {
  const {
    playlist,
    displayedAlbums,
    currentTrack,
    buttonText,
    handleAlbumClick,
    handleVolumeToggle,
    setFullPlayerVisible,
  } = useMusicPlayer();

  // Tells the mini player to hide itself while this full widget is on
  // screen, and to reappear once it's gone.
  useEffect(() => {
    setFullPlayerVisible(true);
    return () => setFullPlayerVisible(false);
  }, [setFullPlayerVisible]);

  if (!playlist.length || !currentTrack) {
    return (
      <div className="bento-item bento-music">
        <div className="widget-content">
          <p>Loading music...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bento-item bento-music">
      <div className="widget-content music-widget">
        {/* Header */}
        <div className="music-header-section">
          <h3 className="music-title">
            On Repeat
            <Repeat size={18} strokeWidth={2.5} />
          </h3>
          <p className="music-tagline">A timeless set of tunes</p>
        </div>

        {/* Album Art */}
        <div className="now-playing-card">
          <img
            src={currentTrack.coverArt}
            alt={currentTrack.album}
            className="now-playing-image"
          />
        </div>

        {/* Now Playing Info Bar */}
        <div className="now-playing-bar">
          <div className="now-playing-info">
            <div className="now-playing-label">Now Playing</div>
            <div className="now-playing-track">{currentTrack.title}</div>
            <div className="now-playing-artist-album">
              <span className="now-playing-artist-name">{currentTrack.artist}</span>
              <span className="now-playing-separator">-</span>
              <span className="now-playing-album-name">{currentTrack.album}</span>
            </div>
          </div>
          <button
            onClick={handleVolumeToggle}
            className="music-volume-button"
          >
            <span key={buttonText} className="button-text-fade">
              {buttonText === 'turn-up' && 'Turn Up'}
              {buttonText === 'turn-down' && 'Turn Down'}
              {buttonText === 'okay' && 'Okay'}
              {buttonText === 'got-it' && 'Got it'}
            </span>
          </button>
        </div>

        {/* Album Grid */}
        <div className="album-grid">
          {displayedAlbums.map((album, index) => (
            <div
              key={`${album.artist}-${album.album}-${index}`}
              className="album-grid-item"
              onClick={() => handleAlbumClick(album)}
            >
              <img
                src={album.coverArt}
                alt={`${album.artist} - ${album.album}`}
                className="album-grid-cover"
              />
              <div className="album-grid-overlay">
                <Play size={20} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MusicPlayer;
